// Admin Panel -> System -> AI Call Log. Proving no patient's identifiers
// reached an AI provider (docs/HMS_V4_AI_PROVIDER_MANAGEMENT.md §3): search
// by UHID/name for every call linked to a patient PLUS any call elsewhere
// whose scanned content named them (a hit there is a leak); otherwise browse
// the whole log with filters. Opening a row's detail is itself audited.
import { useState } from 'react';
import { Search, ShieldAlert, ShieldCheck, Eye } from 'lucide-react';
import { Card, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Input, NativeSelect } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState, EmptyState } from '@/components/ui/States';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { ApiError } from '@/lib/api/client';
import { useAiCallLog, useAiCallLogDetail, useAiCallLogSummary, useRescanAiCallLog } from './hooks';
import type { AiCallLogRow, AiCallLogSearchResult, AiCallLogListResponse, PiiScanStatus } from './types';

const dateTime = (v: string) =>
  new Date(v).toLocaleString('en-IN', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });

// The list endpoint returns a bare array UNLESS the caller opts into the
// paginated envelope (sending ?page=), per apiResponse.js's contract — this
// screen does opt in (see the `page: 1` below), so a plain `!Array.isArray`
// check would misread the envelope `{data, page, limit, ...}` as a search
// result. Check for the shape each response actually carries instead.
function isSearchResult(data: AiCallLogListResponse | undefined): data is AiCallLogSearchResult {
  return !!data && !Array.isArray(data) && 'patient' in data;
}

function rowsOf(data: AiCallLogListResponse | undefined): AiCallLogRow[] {
  if (!data || isSearchResult(data)) return [];
  return Array.isArray(data) ? data : data.data;
}

function PiiBadge({ status }: { status: PiiScanStatus }) {
  switch (status) {
    case 'flagged': return <Badge variant="danger"><ShieldAlert size={12} /> Flagged</Badge>;
    case 'possible': return <Badge variant="warn">Possible</Badge>;
    case 'clean': return <Badge variant="success"><ShieldCheck size={12} /> Clean</Badge>;
    case 'generic_only': return <Badge variant="neutral">Unlinked PII</Badge>;
    case 'not_applicable': return <Badge variant="neutral">—</Badge>;
    default: return <Badge variant="neutral">Not scanned</Badge>;
  }
}

function StatusBadge({ status }: { status: AiCallLogRow['status'] }) {
  if (status === 'success') return <Badge variant="success">Success</Badge>;
  if (status === 'blocked') return <Badge variant="neutral">Blocked</Badge>;
  return <Badge variant="danger">Error</Badge>;
}

function CallLogTable({ rows, onOpen }: { rows: AiCallLogRow[]; onOpen: (id: number) => void }) {
  if (!rows.length) return <EmptyState label="Nothing here" hint="No calls match this filter." />;
  return (
    <div className="overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>When</TableHead>
            <TableHead>Feature</TableHead>
            <TableHead>Patient</TableHead>
            <TableHead>By</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>PII check</TableHead>
            <TableHead />
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((r) => (
            <TableRow key={r.id} className="cursor-pointer" onClick={() => onOpen(r.id)}>
              <TableCell className="whitespace-nowrap text-[12px]">{dateTime(r.created_at)}</TableCell>
              <TableCell className="text-[12px]">
                {r.doc_type || r.capability}
                <div className="text-[11px] text-ink-muted">{r.endpoint}</div>
              </TableCell>
              <TableCell className="text-[12px]">
                {r.patient_name ? `${r.patient_name} · ${r.patient_uhid}` : <span className="text-ink-muted">— not linked —</span>}
              </TableCell>
              <TableCell className="text-[12px]">{r.actor ?? '—'}</TableCell>
              <TableCell><StatusBadge status={r.status} /></TableCell>
              <TableCell><PiiBadge status={r.pii_scan_status} /></TableCell>
              <TableCell><Eye size={14} className="text-ink-muted" /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function CallLogDetailDialog({ id, onClose }: { id: number | null; onClose: () => void }) {
  const detail = useAiCallLogDetail(id);
  return (
    <Dialog open={id != null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-2xl grid-cols-[minmax(0,1fr)] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>AI call detail</DialogTitle>
        </DialogHeader>
        {detail.isLoading ? (
          <LoadingState label="Loading…" />
        ) : detail.isError ? (
          <ErrorState message={detail.error instanceof ApiError ? detail.error.message : 'Could not load this call'} />
        ) : detail.data ? (
          <div className="min-w-0 space-y-3 text-[12px]">
            <div className="flex flex-wrap items-center gap-2">
              <StatusBadge status={detail.data.status} />
              <PiiBadge status={detail.data.pii_scan_status} />
              {detail.data.pseudonymized && <Badge variant="success">Pseudonymised</Badge>}
              <span className="text-ink-muted">{dateTime(detail.data.created_at)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-ink-muted">
              <div>Provider: <span className="text-ink">{detail.data.provider ?? '—'}</span></div>
              <div>Model: <span className="text-ink">{detail.data.model ?? '—'}</span></div>
              <div>Actor: <span className="text-ink">{detail.data.actor ?? '—'}</span></div>
              <div>Consent: <span className="text-ink">{detail.data.consent_status ?? '—'}</span></div>
              <div>Patient: <span className="text-ink">{detail.data.patient_name ? `${detail.data.patient_name} · ${detail.data.patient_uhid}` : 'not linked'}</span></div>
              <div>Branch: <span className="text-ink">{detail.data.branch_id ?? '—'}</span></div>
            </div>

            {!detail.data.content_available ? (
              <div className="rounded-md border border-border bg-muted p-3 text-ink-muted">
                Content removed after 12 months (retention policy). Metadata, usage and the PII verdict above are kept.
              </div>
            ) : (
              <>
                {detail.data.pii_findings && detail.data.pii_findings.length > 0 && (
                  <div className="rounded-md border border-danger/40 bg-danger/5 p-3">
                    <div className="mb-1 font-semibold text-danger-ink">PII findings</div>
                    <ul className="space-y-0.5">
                      {detail.data.pii_findings.map((f, i) => (
                        <li key={i}>{f.type} · {f.field} · char {f.offset} · <code>{f.excerpt}</code></li>
                      ))}
                    </ul>
                  </div>
                )}
                <div>
                  <div className="mb-1 font-semibold">System prompt</div>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] rounded-md border border-border bg-muted p-2 font-mono text-[11px]">
                    {detail.data.system_prompt || '—'}
                  </pre>
                </div>
                <div>
                  <div className="mb-1 font-semibold">Messages</div>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] rounded-md border border-border bg-muted p-2 font-mono text-[11px]">
                    {detail.data.messages ? JSON.stringify(detail.data.messages, null, 2) : '—'}
                  </pre>
                </div>
                <div>
                  <div className="mb-1 font-semibold">AI response</div>
                  <pre className="max-h-40 overflow-auto whitespace-pre-wrap [overflow-wrap:anywhere] rounded-md border border-border bg-muted p-2 font-mono text-[11px]">
                    {detail.data.response || detail.data.error_message || '—'}
                  </pre>
                </div>
              </>
            )}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default function AICallLogTab() {
  const [search, setSearch] = useState('');
  const [endpoint, setEndpoint] = useState('');
  const [status, setStatus] = useState('');
  const [pii, setPii] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  const summary = useAiCallLogSummary();
  const rescan = useRescanAiCallLog();

  const isUhid = /^[A-Za-z0-9]{4,12}$/.test(search.trim()) && !search.includes(' ');
  const log = useAiCallLog({
    uhid: search.trim() && isUhid ? search.trim() : undefined,
    q: search.trim() && !isUhid ? search.trim() : undefined,
    endpoint: endpoint || undefined,
    status: status || undefined,
    pii: pii || undefined,
    page: 1,
  });

  const data = log.data;
  const asSearch = isSearchResult(data) ? data : null;
  const asList = rowsOf(data);

  return (
    <div>
      <div className="mb-4 text-[13px] leading-relaxed text-ink-muted">
        Every AI call this hospital has made, exactly what was sent, and an automatic check that no
        patient's own identifiers reached the provider. Search a UHID or name to see everything about
        that patient — including, separately, anything elsewhere that still named them.
      </div>

      {summary.data && (
        <Card className="mb-4">
          <CardHeader><CardTitle>Last 30 days</CardTitle></CardHeader>
          <div className="flex flex-wrap items-center gap-4 px-4 pb-3 text-[12px] text-ink-muted">
            <span>{summary.data.total} calls</span>
            <span>{summary.data.pseudonymised} pseudonymised</span>
            <span className={summary.data.flagged ? 'font-semibold text-danger-ink' : ''}>
              ⚠ {summary.data.flagged} flagged
            </span>
            <span>{summary.data.not_linked} not linked to a patient</span>
            <span>{summary.data.blocked} blocked</span>
            <Button
              size="sm"
              variant="ghost"
              className="ml-auto"
              disabled={rescan.isPending}
              onClick={() => rescan.mutate({})}
            >
              {rescan.isPending ? 'Scanning…' : 'Re-scan unscanned rows'}
            </Button>
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1 border-t border-border px-4 py-2 text-[11px] text-ink-muted">
            {summary.data.coverage.map((c) => (
              <span key={`${c.capability}-${c.label}`}>
                ✓ {c.label}{c.note ? ` (${c.note})` : ''}
              </span>
            ))}
          </div>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle><Search size={15} /> AI Call Log</CardTitle>
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="h-8 w-56 text-[12px]"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="UHID or patient name…"
            />
            <NativeSelect className="h-8 w-auto text-[12px]" value={endpoint} onChange={(e) => setEndpoint(e.target.value)}>
              <option value="">Any feature</option>
              <option value="generate-document">Documents</option>
              <option value="extract-fields">Voice form fill</option>
              <option value="ip-gist">IP gist</option>
              <option value="extract-intent">Kiosk intake</option>
              <option value="vendor-invoice-extract">Invoices</option>
              <option value="transcribe">Voice transcribe</option>
              <option value="translate">Voice translate</option>
            </NativeSelect>
            <NativeSelect className="h-8 w-auto text-[12px]" value={status} onChange={(e) => setStatus(e.target.value)}>
              <option value="">Any status</option>
              <option value="success">Success</option>
              <option value="error">Error</option>
              <option value="blocked">Blocked</option>
            </NativeSelect>
            <NativeSelect className="h-8 w-auto text-[12px]" value={pii} onChange={(e) => setPii(e.target.value)}>
              <option value="">Any PII check</option>
              <option value="flagged">Flagged</option>
              <option value="possible">Possible</option>
              <option value="clean">Clean</option>
            </NativeSelect>
          </div>
        </CardHeader>

        {log.isError ? (
          <ErrorState
            message={log.error instanceof ApiError ? log.error.message : 'Could not load the AI call log'}
            onRetry={() => log.refetch()}
          />
        ) : log.isLoading ? (
          <LoadingState label="Loading…" />
        ) : asSearch ? (
          <div className="space-y-4 px-4 pb-4">
            <div className="text-[12px] text-ink-muted">
              {asSearch.patient.name} · {asSearch.patient.uhid}
            </div>
            <div className="text-[12px] font-semibold">Calls linked to this patient</div>
            <CallLogTable rows={asSearch.linked} onOpen={setOpenId} />
            <div className="text-[12px] font-semibold text-danger-ink">
              ⚠ Calls whose sent content named this patient — should always be empty
            </div>
            <CallLogTable rows={asSearch.possible_leaks} onOpen={setOpenId} />
          </div>
        ) : (
          <div className="px-4 pb-4">
            <CallLogTable rows={asList} onOpen={setOpenId} />
          </div>
        )}
      </Card>

      <CallLogDetailDialog id={openId} onClose={() => setOpenId(null)} />
    </div>
  );
}
